import {
  Box,
  Typography,
  Button,
  Checkbox,
  IconButton,
  useMediaQuery,
} from '@mui/material'
import useDateRange from 'hooks/useDateRange'
import { OrderSearchBar } from 'components/OrderSearchBar/OrderSearchBar'
import theme from 'lib/theme'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import { Dispatch, SetStateAction, useState } from 'react'
import { useAlert } from 'providers/AlertProvider'
import { useTranslation } from 'react-i18next'
import { useGetUserPlaces } from 'hooks/usePlaces'
import { useAuth } from 'providers/AuthProvider'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { Order } from 'types/api/order'
import { CancelOrdersModal } from './CancelOrdersModal'

type OrdersHeaderProps = {
  placeInfo: { label: string; value: string }[]
  hasListItems: boolean
  setCheckedOrders: Dispatch<
    SetStateAction<{ id: string; visibleId: string }[]>
  >
  checkedOrders: { id: string; visibleId: string }[]
  orders: Order[]
  queries: { [key: string]: string } | null
}

export const OrdersHeader = ({
  placeInfo,
  queries,
  hasListItems,
  setCheckedOrders,
  orders,
  checkedOrders,
}: OrdersHeaderProps) => {
  const navigate = useNavigate()
  const [input, setInput] = useState<string>('')
  const { userStores } = useAuth()
  const enabledCondition = input?.length > 2 ? 'true' : 'false'
  const { isLoading, places, error } = useGetUserPlaces(
    userStores,
    input,
    enabledCondition
  )
  const [modal, setModal] = useState({
    right: false,
  })
  const { setAlert } = useAlert()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dateRange = useDateRange()

  const handleOpenModal = () => {
    if (checkedOrders.length > 150) {
      setAlert(t('cancelOrdersLimit'), 'failure', {
        vertical: 'bottom',
        horizontal: isMobile ? 'center' : 'right',
      })
    } else {
      setModal({ right: true })
    }
  }
  const handleCloseModal = () => {
    setModal({ right: false })
    setCheckedOrders([])
  }

  const allOrdersOnPageChecked = orders.every((o) => {
    return Boolean(checkedOrders.find((co) => co.id === o.id))
  })

  const handleCheckAllOrders = () => {
    if (allOrdersOnPageChecked) {
      const newCheckedOrders = checkedOrders.filter(
        (o) => !orders.find((order) => order.id === o.id)
      )
      setCheckedOrders([...newCheckedOrders])
    } else {
      const newCheckedOrders = orders.map((o) => {
        return { id: o.id, visibleId: o.visibleId }
      })
      setCheckedOrders([...checkedOrders, ...newCheckedOrders])
    }
  }

  const clearSearch = () => {
    setInput('')
    const formattedParams = createSearchParams({
      start_delivery_date: dateRange[0],
      end_delivery_date: dateRange[1],
      page: '1',
    })
    navigate(`/order?${formattedParams}`)
  }
  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        sx={{
          flexFlow: 'row wrap',
        }}>
        <Typography variant='h1' mb={3}>
          {t('orders')}
        </Typography>
        <OrderSearchBar
          placeInfo={placeInfo}
          error={Boolean(error)}
          isLoading={isLoading}
          places={places}
          input={input}
          setInput={setInput}
          placeholderText={t('searchOrdersPlaceholder')}
          queries={queries}
          ariaLabel={t('searchOrders')}
          dateRange={dateRange}
        />
      </Box>
      {Object.keys(queries ?? {})?.length > 0 && (
        <Box>
          <Box>
            <Button
              onClick={clearSearch}
              sx={{
                background: 'transparent',
                color: theme.palette.primary.main,
                padding: 0,
                marginBottom: theme.spacing(1),
                '&:hover': {
                  background: 'transparent',
                  color: theme.palette.primary.dark,
                },
              }}>
              <ArrowBackIcon
                sx={{
                  fill: theme.palette.primary.main,
                  marginRight: theme.spacing(1),
                  '&:hover': { fill: theme.palette.primary.dark },
                }}
              />
              <Typography>{t('allOrders')}</Typography>
            </Button>
            {hasListItems ? (
              <Box display='flex'>
                <Typography
                  variant='h2'
                  sx={{ marginBottom: theme.spacing(1) }}>
                  {t('searchResults')}
                </Typography>
              </Box>
            ) : (
              <Box display='flex'>
                <Typography
                  variant='h2'
                  sx={{ marginBottom: theme.spacing(1) }}>
                  {t('noSearchResultWithQuery')}
                </Typography>
              </Box>
            )}
          </Box>
          {!hasListItems && (
            <Typography variant='body1'>{t('noSearchResults')}</Typography>
          )}
        </Box>
      )}
      <Box display='flex'>
        {orders?.length > 0 && (
          <Checkbox
            sx={{
              marginLeft: theme.spacing(2),
              padding: theme.spacing(1),
              '&.Mui-checked .MuiSvgIcon-root': {
                fill: theme.palette.primary.main,
              },
            }}
            inputProps={{
              'aria-label': t('selectOrderForCancellation'),
            }}
            checked={allOrdersOnPageChecked}
            onClick={handleCheckAllOrders}
          />
        )}
        {checkedOrders.length > 0 && (
          <IconButton
            onClick={() => handleOpenModal()}
            aria-label={t('cancelSelectedOrders')}
            sx={{
              padding: theme.spacing(1),
              width: theme.spacing(5),
              marginLeft: theme.spacing(2),
            }}>
            <DeleteIcon
              sx={{
                fill: theme.palette.icon.main,
                '&:hover': { fill: theme.palette.icon.hover },
              }}
            />
          </IconButton>
        )}
      </Box>
      <CancelOrdersModal
        modal={modal}
        handleCloseModal={handleCloseModal}
        orders={checkedOrders}
      />
    </Box>
  )
}
